// PostHog product-analytics wiring for the Pro app. Init happens once at app
// boot (main.tsx) before React mounts so the very first pageview is captured.
// Keys live in env so dev/preview/prod can point at different projects without
// code changes.
//
// EU host by default — keeps event data inside the EEA so the privacy policy
// stays accurate.

import posthog from "posthog-js";

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://eu.i.posthog.com";

let booted = false;

function isLocalhost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0" || h === "::1";
}

// Public share/invite/e-sign links carry bearer tokens in the path
// (/join/:token, /sign/:token, /space/:token). Mask them so nobody can lift a
// live invite or signing link out of the PostHog event stream.
// (/space/deal/:dealId is the signed-in portal route — an id, not a secret.)
function maskTokenPaths(pathname: string): string {
  return pathname
    .replace(/^\/(join|sign)\/[^/]+/, "/$1/:token")
    .replace(/^\/space\/(?!deal(?:\/|$))[^/]+/, "/space/:token");
}

// Reset-password / verify-email land with secrets in the query string.
function sanitizeUrl(raw: string): string {
  try {
    // No base URL on purpose: non-absolute values (e.g. $referrer's literal
    // "$direct") throw and pass through untouched.
    const u = new URL(raw);
    u.searchParams.delete("token");
    u.searchParams.delete("email");
    u.pathname = maskTokenPaths(u.pathname);
    return u.toString();
  } catch {
    // ignore malformed urls
    return raw;
  }
}

/** True once `posthog.init` actually ran (key present, not localhost/SSR). */
export function isPostHogEnabled(): boolean {
  return booted;
}

export function initPostHog(): void {
  if (booted) return;
  // Never during the SSR/prerender pass — analytics is a browser concern.
  if (typeof window === "undefined") return;
  if (!KEY) {
    // No key configured — quietly skip. Useful in dev / preview deploys.
    return;
  }
  if (isLocalhost()) {
    // Skip analytics on localhost so dev noise doesn't pollute prod data.
    return;
  }
  posthog.init(KEY, {
    api_host: HOST,
    capture_pageview: "history_change", // SPA-friendly: capture on router nav
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    autocapture: true,
    // Privacy: strip secrets and personal hints from URL-shaped properties
    // before anything leaves the browser.
    sanitize_properties: (props) => {
      for (const key of ["$current_url", "$referrer", "$initial_referrer"]) {
        const value = props[key];
        if (typeof value === "string") props[key] = sanitizeUrl(value);
      }
      for (const key of ["$pathname", "$prev_pageview_pathname"]) {
        const value = props[key];
        if (typeof value === "string") props[key] = maskTokenPaths(value);
      }
      return props;
    },
  });
  booted = true;
}

export { posthog };
